import React, { useState } from 'react'

import { getIntegrationDisplayName, type IntegrationServiceId } from '../../common/integrations'
import { useCurrentUserIntegrations } from '../../views/Integration/useCurrentUserIntegrations'
import { useLastUsedDocumentConnector } from '../../common/lastUsedDocumentConnector'
import { type ButtonVariant } from '../Button/Button'
import { DocumentConnectorActionButton, type DocumentConnectorActionItem } from './DocumentConnectorActionButton'
import { SaveToDocumentConnectorModal } from '../../apps/professional-app/fa/MeetingSummary/Header/SaveToDocumentConnectorModal'
import { type DocumentExportType } from './models'
import { type DocumentConnectorAnchor, type DocumentConnectorItem } from '../../store/documentConnectors/documentConnectors'
import { type User } from '../../models/User'

export interface SaveToDocumentConnectorVariant {
  label: string
  generatePDFBlob: () => Promise<Blob | null>
  defaultFileName: string
}

interface SaveToDocumentConnectorButtonProps {
  variants: SaveToDocumentConnectorVariant[]
  disabled?: boolean
  buttonClassName?: string
  buttonVariant?: ButtonVariant
  initialFolderIdsByServiceId?: Partial<Record<IntegrationServiceId, string[]>>
  initialFoldersReady?: boolean
  anchor?: DocumentConnectorAnchor | null
  saveSyncStatus?: boolean
  onSaved?: (uploaded: DocumentConnectorItem) => void | Promise<void>
  showSuccessState?: boolean
  showSuccessSidebar?: boolean
  contact?: User | null
  onContactUpdated?: (user: User) => void
  exportType: DocumentExportType
}

interface SaveToDocumentConnectorSelection {
  serviceId: IntegrationServiceId
  variant: SaveToDocumentConnectorVariant
}

export const SaveToDocumentConnectorButton: React.FC<SaveToDocumentConnectorButtonProps> = ({
  variants,
  disabled,
  buttonClassName,
  buttonVariant = 'header',
  initialFolderIdsByServiceId,
  initialFoldersReady,
  anchor,
  saveSyncStatus,
  onSaved,
  showSuccessState,
  showSuccessSidebar,
  contact,
  onContactUpdated,
  exportType,
}) => {
  const { documentUploadSupportedIntegrations } = useCurrentUserIntegrations()
  const [lastUsedServiceId, recordLastUsedServiceId] = useLastUsedDocumentConnector()

  const [selected, setSelected] = useState<SaveToDocumentConnectorSelection | null>(null)

  if (documentUploadSupportedIntegrations.length === 0 || variants.length === 0) {
    return null
  }

  const selectionByKey = new Map<string, SaveToDocumentConnectorSelection>()
  const items: DocumentConnectorActionItem[] = documentUploadSupportedIntegrations.flatMap(({ serviceId }) =>
    variants.map((variant) => {
      const key = `${serviceId}::${variant.label}`
      selectionByKey.set(key, { serviceId, variant })
      const displayName = getIntegrationDisplayName(serviceId)
      return { key, serviceId, label: variants.length > 1 ? `${variant.label} — Save to ${displayName}` : `Save to ${displayName}` }
    }),
  )

  const primaryKey = items.find((item) => item.serviceId === lastUsedServiceId)?.key

  const handleSelect = (key: string) => {
    const selection = selectionByKey.get(key)
    if (!selection) {
      return
    }
    recordLastUsedServiceId(selection.serviceId)
    setSelected(selection)
  }

  return (
    <>
      {selected ? (
        <SaveToDocumentConnectorModal
          key={`${selected.serviceId}::${selected.variant.label}`}
          serviceId={selected.serviceId}
          show={true}
          onHide={() => setSelected(null)}
          generatePDFBlob={selected.variant.generatePDFBlob}
          defaultFileName={selected.variant.defaultFileName}
          initialFolderIds={initialFolderIdsByServiceId?.[selected.serviceId]}
          initialFoldersReady={initialFoldersReady}
          anchor={anchor}
          saveSyncStatus={saveSyncStatus}
          onSaved={onSaved}
          showSuccessState={showSuccessState}
          showSuccessSidebar={showSuccessSidebar}
          contact={contact}
          onContactUpdated={onContactUpdated}
          exportType={exportType}
        />
      ) : null}
      <DocumentConnectorActionButton
        items={items}
        onSelect={handleSelect}
        primaryKey={primaryKey}
        disabled={disabled}
        buttonClassName={buttonClassName}
        buttonVariant={buttonVariant}
      />
    </>
  )
}
